import moment from 'moment';

import { getUtcOffset } from '../../../utils/common';
import { FORTA_BLUE } from '../../../utils/colors';
import {
  CHILD_BACKGROUND_CONFIGS,
  SCHEDULE_ACTIVITY_COLORS,
  SCHEDULE_ACTIVITY_OPTIONS,
  SCHEDULE_ACTIVITY_TYPE,
} from './constants';

import type { FWeeklyScheduleItem } from '../../../components/baseV2';
import type { SelectionEntry } from '../components';
import type {
  ChildBackgroundConfig,
  ChildBackgroundType,
  ChildFieldPresence,
  ChildSchedulePayload,
  ChildScheduleSlot,
  IChildBackground,
  IChildInfo,
  IChildSchedule,
} from './types';

export function getChildBackgroundEntries(
  backgrounds: ReadonlyArray<IChildBackground> | null | undefined,
  type: ChildBackgroundType,
): SelectionEntry[] {
  return (backgrounds ?? [])
    .filter((background) => background.background_type === type)
    .map((background) => ({ id: background.id, value: background.background_description }));
}

function meetsChildBackgroundMinItems(
  backgrounds: ReadonlyArray<IChildBackground> | null | undefined,
  config: ChildBackgroundConfig,
): boolean {
  return (
    (backgrounds ?? []).filter((background) => background.background_type === config.type).length >=
    (config.minItems ?? 1)
  );
}

export function hasChildFieldValue(childInfo: ChildFieldPresence | null | undefined): boolean {
  if (!childInfo) {
    return false;
  }

  return (
    Boolean(childInfo.child_behavior_issues) ||
    (childInfo.backgrounds?.length ?? 0) > 0 ||
    (childInfo.schedule?.length ?? 0) > 0
  );
}

export function hasChildBackgroundErrors(
  childInfo: Pick<IChildInfo, 'backgrounds'> | null | undefined,
): boolean {
  if (!childInfo) {
    return false;
  }

  return CHILD_BACKGROUND_CONFIGS.some(
    (config) => !meetsChildBackgroundMinItems(childInfo.backgrounds, config),
  );
}

export function isChildBackgroundComplete(childInfo: IChildInfo | null | undefined): boolean {
  if (!childInfo) {
    return false;
  }

  return CHILD_BACKGROUND_CONFIGS.every((config) =>
    meetsChildBackgroundMinItems(childInfo.backgrounds, config),
  );
}

export function getScheduleValidationErrors(
  schedules: ReadonlyArray<IChildSchedule> | null | undefined,
  notAttendingSchool: boolean,
): string[] {
  const errors: string[] = [];
  const scheduleList = schedules ?? [];
  const hasFortaTherapy = scheduleList.some(
    (schedule) => schedule.schedule_activity_type === SCHEDULE_ACTIVITY_TYPE.FORTA_THERAPY,
  );
  const hasSchool = scheduleList.some(
    (schedule) => schedule.schedule_activity_type === SCHEDULE_ACTIVITY_TYPE.SCHOOL,
  );

  if (!hasFortaTherapy) {
    errors.push('Add at least one "Available for Forta therapy" time slot.');
  }
  if (!notAttendingSchool && !hasSchool) {
    errors.push('Add at least one "School" time slot, or mark the child as not attending school.');
  }
  if (notAttendingSchool && hasSchool) {
    errors.push('Remove all "School" time slots because the child is not attending school.');
  }

  return errors;
}

export function hasChildScheduleErrors(
  childInfo: Pick<IChildInfo, 'schedule'> | null | undefined,
  notAttendingSchool: boolean,
): boolean {
  if (!childInfo) {
    return false;
  }

  return getScheduleValidationErrors(childInfo.schedule, notAttendingSchool).length > 0;
}

export function toChildSchedulePayload({
  dayOfWeek,
  activityType,
  startHour,
  endHour,
}: ChildScheduleSlot): ChildSchedulePayload {
  const startTime = moment.utc().startOf('day').add(startHour, 'hours');
  const endTime = moment.utc().startOf('day').add(endHour, 'hours');
  return {
    day_of_week: dayOfWeek,
    schedule_activity_type: activityType,
    schedule_start_date: startTime.toISOString(),
    schedule_end_date: endTime.toISOString(),
    utc_offset: getUtcOffset(startTime),
  };
}

export function toWeeklyScheduleItems(
  schedules: ReadonlyArray<IChildSchedule>,
): FWeeklyScheduleItem[] {
  return schedules.map((schedule) => ({
    id: schedule.id,
    schedule_start_date: schedule.schedule_start_date,
    schedule_end_date: schedule.schedule_end_date,
    day_of_week: schedule.day_of_week,
    schedule_activity_type: schedule.schedule_activity_type,
    activity_label:
      SCHEDULE_ACTIVITY_OPTIONS.find((option) => option.value === schedule.schedule_activity_type)
        ?.label ?? schedule.schedule_activity_type,
    activity_color:
      Object.entries(SCHEDULE_ACTIVITY_COLORS).find(
        ([activityType]) => activityType === schedule.schedule_activity_type,
      )?.[1] ?? FORTA_BLUE.primary,
  }));
}
